import React, { useState } from "react";
import SEO from "../components/SEO";
import ContactForm from "../components/ContactForm";
import LightboxModal from "../components/LightboxModal";
import { getOptimizedImageUrl } from "../lib/cms";

export default function Redekasser() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const images = [
    {
      url: "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/redekasse-klatreudstyr.webp",
      alt: "Montering af brun redekasse højt i træ med professionelt klatreudstyr, reb og karabiner",
      caption: "Opsætning i stor højde med klatreudstyr",
    },
    {
      url: "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/redekasse-groen.webp",
      alt: "Stor mørkegrøn redekasse monteret solidt direkte på træstamme",
      caption: "Montering af specialkasse direkte på stammen",
    },
    {
      url: "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/trae-til-redekasse.webp",
      alt: "Højt træ i fuld højde velegnet til professionel opsætning af redekasser",
      caption: "Vurdering af egnet træ og placering",
    },
    {
      url: "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/trae-ved-so.webp",
      alt: "Naturligt område ved sø med store træer ideelt til fugle og redekasser",
      caption: "Levesteder og natur i balance",
    },
  ];

  const imageUrls = images.map((img) => img.url);

  const handleOpenImage = (index) => {
    setSelectedIdx(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <SEO
        title="Opsætning af redekasser i træer - Caspers Træservice"
        description="Giv fuglene et sikkert sted at bo – også højt oppe i træerne. Professionel opsætning af uglekasser, tårnfalkekasser og andre redekasser med klatreudstyr."
      />

      {/* Hero Section */}
      <section className="relative bg-[#12281D] text-white py-16 sm:py-24 border-b border-[#1E4231] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/redekasse-klatreudstyr.webp",
              1600,
              80
            )}
            alt="Opsætning af redekasse i træ"
            className="w-full h-full object-cover opacity-25 object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12281D] via-[#12281D]/80 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
              Opsætning af redekasser i træer
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-[#C0DACB] leading-relaxed">
              Giv fuglene et sikkert sted at bo – også højt oppe i træerne
            </p>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <a
                href="tel:42169230"
                className="inline-flex items-center gap-2.5 bg-[#D9691E] hover:bg-[#C25B15] text-white px-6 py-3.5 rounded-xl font-bold text-sm sm:text-base shadow-lg transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 42 16 92 30</span>
              </a>
              <a
                href="#kontakt-formular"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded-xl font-semibold text-sm sm:text-base border border-white/20 transition-all whitespace-nowrap"
              >
                <span>Få uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 sm:py-24 bg-[#F2EDE2]/70 border-y border-[#E2D8C8] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            {/* Image Side: Vises i venstre side med overlap bag boksen */}
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0">
              <div className="relative rounded-2xl overflow-hidden h-[460px] sm:h-[540px] lg:h-[720px] w-full shadow-lg">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/redekasse-klatreudstyr.webp",
                    1200,
                    85
                  )}
                  alt="Opsætning af redekasse med professionelt klatreudstyr i højt træ"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

            {/* Content Side: Boks med baggrundsfarve #1e5d31, overlapper billedet foran */}
            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10 bg-[#1e5d31] rounded-2xl p-6 sm:p-9 lg:p-12 space-y-6 shadow-none border-0">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                Giv fuglene et sikkert sted at bo – også højt oppe i træerne
              </h2>

              <div className="space-y-4 text-base sm:text-lg text-[#D5E5DB] leading-relaxed font-normal">
                <p>
                  Har du en redekasse, der skal placeres højt i et træ? Hos Caspers Træservice hjælper jeg med opsætning af redekasser i træer – også hvor kassen skal placeres i større højde eller på steder, der er vanskelige at komme til fra jorden. Med professionelt klatreudstyr kan jeg komme sikkert op i træet og placere kassen, hvor den passer bedst til formålet.
                </p>
              </div>

              {/* Underoverskrift 1: Redekasser til forskellige arter */}
              <div className="p-6 rounded-2xl bg-white/10 border border-white/15 space-y-4">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  Redekasser til forskellige arter
                </h3>
                <p className="text-sm sm:text-base text-[#D5E5DB] leading-relaxed">
                  Jeg kan blandt andet hjælpe med opsætning af:
                </p>
                <ul className="space-y-2.5 text-sm sm:text-base text-white">
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#D9691E] flex items-center justify-center shrink-0 mt-0.5 text-xs text-white">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </span>
                    <span>Uglekasser til eksempelvis natugle</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#D9691E] flex items-center justify-center shrink-0 mt-0.5 text-xs text-white">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </span>
                    <span>Tårnfalke-kasser</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#D9691E] flex items-center justify-center shrink-0 mt-0.5 text-xs text-white">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </span>
                    <span>Skallesluger-kasser</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#D9691E] flex items-center justify-center shrink-0 mt-0.5 text-xs text-white">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </span>
                    <span>Andre specialkasser til fugle og naturformål</span>
                  </li>
                </ul>
                <p className="text-sm sm:text-base text-[#D5E5DB] leading-relaxed pt-2">
                  Placeringen har stor betydning for, om kassen bliver attraktiv for fuglene. Jeg har et tæt samarbejde med en erfaren ornitolog og kan på den måde rådgive om den bedste løsning til lige netop din fuglekasses placering, højde og adgang til kassen ud fra træets størrelse og omgivelserne.
                </p>
              </div>

              {/* Underoverskrift 2: Når redekassen skal sidde højt */}
              <div className="space-y-3 pt-2">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  Når redekassen skal sidde højt
                </h3>
                <div className="space-y-3 text-base sm:text-lg text-[#D5E5DB] leading-relaxed font-normal">
                  <p>
                    Nogle fuglekasser skal placeres højt for at give fuglene de rette forhold. Det kan være svært eller direkte uforsvarligt at komme til med en almindelig stige eller lift.
                  </p>
                  <p>
                    Her kommer min erfaring med træklatring til sin ret. Jeg kan klatre sikkert op i træet og montere kassen.
                  </p>
                  <p>
                    Når en fuglekasse monteres i et levende træ, er det vigtigt at tage hensyn til både træet og fuglene.
                  </p>
                  <p className="bg-white/10 p-5 rounded-xl border border-white/15 font-medium text-white">
                    Jeg sørger for, at arbejdet udføres sikkert og med mindst mulig påvirkning af træet. Placeringen tilpasses træets struktur og de omkringliggende forhold.
                  </p>
                </div>
              </div>

              {/* Underoverskrift 4: Skal jeg hjælpe med opsætningen? */}
              <div className="p-6 rounded-2xl bg-white/10 border border-white/15 space-y-4">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  Skal jeg hjælpe med opsætningen?
                </h3>
                <div className="space-y-3 text-base text-[#D5E5DB] leading-relaxed font-normal">
                  <p>
                    Har du en redekasse, der skal op i et træ – eller overvejer du at opsætte en uglekasse eller tårnfalkekasse?
                  </p>
                  <p>
                    Kontakt Caspers Træservice for en snak om mulighederne.
                  </p>
                  <p>
                    Jeg kan vurderer træet og placeringen og finder sammen med dig en løsning, der passer til både fuglene, træet og omgivelserne.
                  </p>
                </div>

                <div className="pt-2 flex flex-wrap gap-3">
                  <a
                    href="tel:42169230"
                    className="inline-flex items-center gap-2 bg-[#D9691E] hover:bg-[#C25B15] text-white px-5 py-3 rounded-xl font-bold text-sm shadow transition-all"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    <span>Ring 42 16 92 30</span>
                  </a>
                  <a
                    href="#kontakt-formular"
                    className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-5 py-3 rounded-xl font-semibold text-sm transition-all"
                  >
                    <span>Skriv en besked</span>
                    <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Galleri med de 4 medfølgende billeder */}
      <section className="py-16 sm:py-20 bg-[#FBF9F5] border-t border-[#E5DDCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#12281D] tracking-tight">
              Billeder fra opsætning af redekasser
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[#4E685A] leading-relaxed">
              Professionel klatring og omhyggelig montering i harmoni med træet og naturen
            </p>
            <div className="w-16 h-1 bg-[#D9691E] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {images.map((item, idx) => (
              <div
                key={idx}
                onClick={() => handleOpenImage(idx)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenImage(idx);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Åbn billede: ${item.caption}`}
                className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-[#E7DFD2] shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#D9691E]"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-[#ECE6DB]">
                  <img
                    src={getOptimizedImageUrl(item.url, 800, 85)}
                    alt={item.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#0E1F16]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center p-4">
                    <span className="w-11 h-11 rounded-full bg-[#1E4231]/90 text-white flex items-center justify-center text-sm shadow-md border border-white/20 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-200">
                      <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-white border-t border-[#EFE9DF]">
                  <p className="text-sm font-semibold text-[#14291E] leading-snug">
                    {item.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <LightboxModal
          images={imageUrls}
          currentIndex={selectedIdx}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(newIdx) => setSelectedIdx(newIdx)}
        />
      </section>

      {/* Contact Section With Form */}
      <section className="py-16 sm:py-24 bg-[#14291E] text-white relative overflow-hidden" id="kontakt-formular">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            {/* Direct Reassurance Side (Overlap style) */}
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0">
              <div className="bg-[#1C3B2C] rounded-2xl p-6 sm:p-9 lg:p-12 space-y-6 border border-[#2B5440]">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D9691E]/20 text-[#F39C5A] text-xs font-bold uppercase tracking-wider">
                  <i className="fa-solid fa-feather" aria-hidden="true" />
                  <span>Opsætning af redekasser</span>
                </div>

                <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                  Skal jeg hjælpe med opsætningen?
                </h2>

                <p className="text-[#C0DACB] text-base sm:text-lg leading-relaxed">
                  Har du en redekasse, der skal op i et træ – eller overvejer du at opsætte en uglekasse eller tårnfalkekasse? Udfyld formularen, eller ring direkte til Casper på telefonen.
                </p>

                <div className="space-y-4 pt-4 border-t border-[#29513D]">
                  <a
                    href="tel:42169230"
                    className="flex items-center gap-4 group p-3 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#D9691E] text-white flex items-center justify-center text-xl shrink-0 group-hover:scale-105 transition-transform">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#9BBBAA] uppercase tracking-wider block font-medium">
                        Direkte kontakt
                      </span>
                      <span className="text-xl font-bold text-white group-hover:text-[#F39C5A] transition-colors">
                        42 16 92 30
                      </span>
                    </div>
                  </a>

                  <a
                    href="mailto:info@casperstraeservice.dk"
                    className="flex items-center gap-4 group p-3 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white/10 text-white flex items-center justify-center text-xl shrink-0 group-hover:scale-105 transition-transform">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#9BBBAA] uppercase tracking-wider block font-medium">
                        Send en email
                      </span>
                      <span className="text-base font-semibold text-white group-hover:text-[#F39C5A] transition-colors">
                        info@casperstraeservice.dk
                      </span>
                    </div>
                  </a>
                </div>

                <div className="p-4 rounded-xl bg-[#142B20] border border-[#254A37] text-xs text-[#ADC5B8] flex items-center gap-3">
                  <i className="fa-solid fa-shield-halved text-[#D9691E] text-base shrink-0" aria-hidden="true" />
                  <span>Caspers Træservice er fuldt forsikret hos Topdanmark op til 10 mio. kr.</span>
                </div>
              </div>
            </div>

            {/* Form Side */}
            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10">
              <ContactForm defaultService="Opsætning af redekasse" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
